import React, { useState, useRef } from 'react';
import { Alert } from 'reactstrap';
import type { Company } from '../../../../../models/companies/kyccs/kycc';
import type { Condition } from '../../../../../models/companies/kyccs/condition';
import { defaultCondition } from '../../../../../models/companies/kyccs/condition';
import { KyccClient } from '../../../../../models/companies/kyccs/kyccClient';
import { Conditions } from './conditions';
import { Checking } from '../checking';
import { Results } from './results';
import { validate } from '../../../../../models/companies/kyccs/kycc';
import { handleAxiosError } from '../../../../../../common/helpers/axiosHelper';

const Status = {
  start: 0,
  checking: 1,
  done: 2,
};

type Props = {
  target: number;
  kyccMonthlyLimit: number;
  kyccCheckedCount: number;
};

export default function ShowView(props: Props) {
  const { target, kyccMonthlyLimit, kyccCheckedCount } = props;
  const kyccClient = useRef(new KyccClient());

  const [company, setCompany] = useState<Company>({
    id: null,
    companyId: null,
    conditions: [defaultCondition],
    kyccMonthlyLimit: kyccMonthlyLimit,
    kyccCheckedCount: kyccCheckedCount,
    errors: {},
  });
  const [status, setStatus] = useState<number>(Status.start);

  const onAddCondition = () => {
    setCompany(prev => ({
      ...prev,
      conditions: [...prev.conditions, defaultCondition],
    }));
  };

  const onChangeCondition = (index: number, condition: Condition) => {
    setCompany(prev => ({
      ...prev,
      conditions: prev.conditions.map((item, i) =>
        i === index ? condition : item,
      ),
    }));
  };

  const onRemoveCondition = (index: number) => {
    setCompany(prev => {
      const newConditions = [...prev.conditions];
      newConditions.splice(index, 1);
      return { ...prev, conditions: newConditions };
    });
  };

  const onStartCheck = () => {
    if (!validate(company)) {
      setCompany(prev => ({
        ...prev,
        conditions: [...prev.conditions],
      }));
      return;
    }

    setStatus(Status.checking);

    kyccClient.current
      .checkCompany(target, company)
      .then(response => {
        setCompany(prev => ({ ...prev, ...response.data }));
        setStatus(Status.done);
      })
      .catch(error => {
        let newErrors: Record<string, string[]> = {};

        if (error.code === 'ECONNABORTED') {
          newErrors['base'] = [
            '接続がタイムアウトしました。検索結果が多すぎる可能性があります。条件を絞ってお試しください。',
          ];
        } else {
          if (error.response.status === 422) {
            newErrors = error.response.data;
          } else if (error.response.status == 500) {
            newErrors['base'] = [error.response.data.message];
            handleAxiosError(error);
          }
        }

        setCompany(prev => ({ ...prev, errors: newErrors }));
        setStatus(Status.start);
      });
  };

  if (kyccCheckedCount >= kyccMonthlyLimit) {
    return <Alert color="danger">今月の検索上限に到達しています</Alert>;
  } else {
    if (status === Status.start) {
      return (
        <Conditions
          target={target}
          company={company}
          onAddCondition={onAddCondition}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
          onStartCheck={onStartCheck}
        />
      );
    } else if (status === Status.checking) {
      return <Checking />;
    } else if (status === Status.done) {
      return <Results company={company} />;
    } else {
      return null;
    }
  }
}
